/**
 * Add User Modal (Phase 30 Wave 4 chunk 30.4.3c)
 *
 * Super-admin-only modal for adding the first (or another) user to an
 * existing organization. Hosted by `OrganizationDetailPanel` inside the
 * Organizations expanded row.
 *
 * Pattern reference: `CreateOrganizationModal.tsx` (chunk 30.4.3b)
 *  - Backdrop (click-to-close) + centered card
 *  - Escape closes the modal (when not submitting)
 *  - `bg-white` on every form input
 *  - `disabled` on inputs + buttons during submission
 *  - Inline red-banner submit error
 *
 * Validation:
 *  - email: required, valid email
 *  - fullName: required, 1-255 chars
 *  - role: required, one of admin/trainer/manager/operator. `super_admin` is
 *    deliberately excluded — backend `createOrgUserSchema` enum at
 *    `apps/api/src/routes/admin/orgs.ts:90` rejects it.
 *
 * Server responses surfaced:
 *  - 409 conflict (email already exists in this org / another org) → field-level
 *    error on email input.
 *  - `meta.auth0UserExisted: true` → orphan-recovery info banner inside the
 *    modal (orphan was found in Auth0 and re-linked to this org).
 *  - All other failures → submit-level error banner.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, UserPlus, X } from 'lucide-react';
import {
  useAddOrgUser,
  type AssignableRole,
} from '../../../hooks/useOrganizations';
import { ApiError } from '../../../lib/api';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  orgName: string;
  /** Called after a successful add so the parent can show a success banner. */
  onSuccess?: (result: {
    email: string;
    passwordResetSent: boolean;
    auth0UserExisted: boolean;
  }) => void;
}

interface FormData {
  email: string;
  fullName: string;
  role: AssignableRole;
}

const initialFormData: FormData = {
  email: '',
  fullName: '',
  role: 'manager',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AddUserModal({
  isOpen,
  onClose,
  orgId,
  orgName,
  onSuccess,
}: AddUserModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailFieldError, setEmailFieldError] = useState<string | null>(null);
  const [fullNameFieldError, setFullNameFieldError] = useState<string | null>(
    null
  );

  const firstInputRef = useRef<HTMLInputElement>(null);

  const addUserMutation = useAddOrgUser(orgId);
  const isSubmitting = addUserMutation.isPending;

  // Reset state shortly after the modal closes so the next open has a clean
  // form (mirrors `CreateOrganizationModal.tsx`).
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setFormData(initialFormData);
        setSubmitError(null);
        setEmailFieldError(null);
        setFullNameFieldError(null);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Focus the first input on open.
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen]);

  // Escape closes the modal (only when not submitting).
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  const validate = (): boolean => {
    let ok = true;
    setEmailFieldError(null);
    setFullNameFieldError(null);
    setSubmitError(null);

    const email = formData.email.trim();
    const fullName = formData.fullName.trim();

    if (!email) {
      setEmailFieldError('Email is required');
      ok = false;
    } else if (email.length > 255) {
      setEmailFieldError('Email must be 255 characters or fewer');
      ok = false;
    } else if (!EMAIL_REGEX.test(email)) {
      setEmailFieldError('Enter a valid email address');
      ok = false;
    }

    if (!fullName) {
      setFullNameFieldError('Full name is required');
      ok = false;
    } else if (fullName.length > 255) {
      setFullNameFieldError('Full name must be 255 characters or fewer');
      ok = false;
    }

    return ok;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    try {
      const result = await addUserMutation.mutateAsync({
        email: formData.email.trim(),
        fullName: formData.fullName.trim(),
        role: formData.role,
      });
      onSuccess?.({
        email: formData.email.trim(),
        passwordResetSent: result.meta.passwordResetSent,
        auth0UserExisted: result.meta.auth0UserExisted,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Backend surfaces both same-org conflict and cross-org conflict at
        // 409. Both are user-correctable on the email input.
        setEmailFieldError(err.message);
      } else if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Failed to add user');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-user-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={!isSubmitting ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 flex items-center justify-center shadow-lg shadow-guild-green/20">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2
                id="add-user-modal-title"
                className="text-xl font-bold text-gray-900 tracking-tight"
              >
                Add User
              </h2>
              <p className="text-sm text-gray-500">
                Onboard a new user into{' '}
                <span className="font-semibold text-gray-700">{orgName}</span>
              </p>
            </div>
          </div>

          {!isSubmitting && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-6 right-6 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-8 py-6">
            {/* Submit-level error banner */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <X className="w-3 h-3 text-red-500" />
                </div>
                <p className="text-red-700 text-sm">{submitError}</p>
              </div>
            )}

            <div className="space-y-5">
              {/* Email */}
              <div>
                <label
                  htmlFor="add-user-email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-user-email"
                  ref={firstInputRef}
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  disabled={isSubmitting}
                  placeholder="user@example.com"
                  maxLength={255}
                  autoComplete="off"
                  aria-invalid={emailFieldError ? true : undefined}
                  aria-describedby={
                    emailFieldError ? 'add-user-email-error' : undefined
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 outline-none transition-all text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500 bg-white ${
                    emailFieldError
                      ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                      : 'border-gray-200 focus:ring-guild-green/20 focus:border-guild-green'
                  }`}
                />
                {emailFieldError && (
                  <p
                    id="add-user-email-error"
                    className="mt-1.5 text-xs text-red-600"
                  >
                    {emailFieldError}
                  </p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label
                  htmlFor="add-user-full-name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-user-full-name"
                  type="text"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      fullName: e.target.value,
                    }))
                  }
                  disabled={isSubmitting}
                  placeholder="Jane Doe"
                  maxLength={255}
                  aria-invalid={fullNameFieldError ? true : undefined}
                  aria-describedby={
                    fullNameFieldError ? 'add-user-full-name-error' : undefined
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 outline-none transition-all text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500 bg-white ${
                    fullNameFieldError
                      ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                      : 'border-gray-200 focus:ring-guild-green/20 focus:border-guild-green'
                  }`}
                />
                {fullNameFieldError && (
                  <p
                    id="add-user-full-name-error"
                    className="mt-1.5 text-xs text-red-600"
                  >
                    {fullNameFieldError}
                  </p>
                )}
              </div>

              {/* Role */}
              <div>
                <label
                  htmlFor="add-user-role"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="add-user-role"
                  value={formData.role}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      role: e.target.value as AssignableRole,
                    }))
                  }
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-guild-green/20 focus:border-guild-green outline-none transition-all text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 bg-white"
                >
                  <option value="admin">Admin (5)</option>
                  <option value="trainer">Trainer (4)</option>
                  <option value="manager">Manager (3)</option>
                  <option value="operator">Operator (1)</option>
                </select>
                <p className="mt-1.5 text-xs text-gray-500">
                  Super admins cannot be created via this flow.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-5 py-2.5 text-gray-600 font-semibold hover:text-gray-900 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 bg-guild-green text-white font-semibold rounded-xl hover:bg-guild-green/95 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-guild-green/20"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddUserModal;
